import React, { useRef, useEffect } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { FaShip, FaTruck, FaCheckCircle, FaGlobe } from 'react-icons/fa';

const ValueProposition = () => {
  const services = [
    {
      icon: <FaShip size={40} className="text-primary" />,
      title: "Importation",
      description: "Gestion des processus d'importation et conformité aux normes canadiennes."
    },
    {
      icon: <FaTruck size={40} className="text-primary" />,
      title: "Logistique",
      description: "Réseau de distribution efficace avec livraison rapide dans tout le Canada."
    },
    {
      icon: <FaCheckCircle size={40} className="text-primary" />,
      title: "Conformité",
      description: "Respect des normes ACIA, ASFC et étiquetage bilingue pour le marché canadien."
    },
    {
      icon: <FaGlobe size={40} className="text-primary" />,
      title: "Chaîne d'approvisionnement",
      description: "Gestion complète de la chaîne d'approvisionnement internationale."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const logosContainerRef = useRef(null);
  const isInView = useInView(logosContainerRef, { once: false, amount: 0.3 });
  const scrollControls = useAnimation();
  
  useEffect(() => {
    if (isInView) {
      // Start the automatic scrolling animation when in view
      scrollControls.start({
        x: [0, -1000],
        transition: {
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: 20,
            ease: "linear",
          },
        },
      });
    } else {
      scrollControls.stop();
    }
  }, [isInView, scrollControls]);

  // Duplicate the logos for a seamless infinite scroll effect
  const logos = [
    { src: "/images/clients/thala.png", alt: "thala", className: "h-24 w-auto object-contain" },
    { src: "/images/clients/huilerie_kiared.png", alt: "huilerie kiared", className: "h-24 w-auto object-contain" },
    { src: "/images/clients/el_walima.png", alt: "el walima", className: "h-20 w-auto object-contain" },
    { src: "/images/clients/azemmour.png", alt: "azemmour ouagued", className: "h-24 w-auto object-contain" },
    { src: "/images/clients/benna.png", alt: "benna", className: "h-20 w-auto object-contain" },
    { src: "/images/clients/huile_dor.png", alt: "Huile d'or", className: "h-24 w-auto object-contain" },
  ];
  
  // Duplicate logos for seamless scrolling
  const allLogos = [...logos, ...logos, ...logos];

  return (
    <section className="section bg-light">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="section-title">Notre Expertise</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            DZF Distribution vous offre des solutions complètes pour développer vos produits sur le marché canadien.
          </p>
        </div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {services.map((service, index) => (
            <motion.div 
              key={index} 
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              variants={itemVariants}
            >
              <div className="mb-4">{service.icon}</div>
              <h3 className="text-xl font-bold mb-3">{service.title}</h3>
              <p className="text-gray-600">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Partners/Clients Logos - Improved with horizontal scrolling */}
        <div className="mt-16" ref={logosContainerRef}>
          <h3 className="text-xl md:text-2xl font-semibold text-center mb-8">
            Ils nous font confiance
          </h3>
          
          <div className="overflow-hidden relative">
            <motion.div 
              className="flex items-center gap-12 md:gap-16 py-1"
              animate={scrollControls}
              style={{ width: "max-content" }}
              whileHover={{ animationPlayState: "paused" }}
            >
              {allLogos.map((logo, index) => (
                <div 
                  key={index} 
                  className="flex justify-center items-center px-4"
                >
                  <img 
                    src={logo.src} 
                    alt={logo.alt} 
                    className={`${logo.className} transition-all hover:scale-110`} 
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueProposition; 